import React from "react";
import ReactionBar from "../../../components/reactions/ReactionBar";
import AOTDComments from "../sections/comments/AotdComments";
import SignedOutComments from "./SignedOutComments";
import AOTDStats from "../sections/stats/AotdStats";
import Verifier from "./shared/Verifier";
import EmojisBox from "./shared/EmojisBox";
import RaceAuditPanel from "./RaceAuditPanel";
import CliqueAttribution from "./shared/CliqueAttribution";
import type { ArtInfo } from "../api/types";
import { QUIZ_ANCHOR_ID } from "../sections/quiz/quizModel";

/** AOTD guessing: the "?" box on the art takes you down to the game. */
function scrollToGuessing() {
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  document
    .getElementById(QUIZ_ANCHOR_ID)
    ?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" });
}

export interface AotdArtCardProps {
  artInfo: ArtInfo;
  artUrl: string | null;
  streakIcon: URL | null;
  generationElapsedTime?: string;
  hasPermission: boolean;
  isAuthenticated: boolean;
  currentUsername: string | null;
  onTriggerWayback: () => void | Promise<void>;
  /** If true, hide the `nextAOTDEmojis` chip from the stats strip. Used by the
   *  `not_started_scheduled` branch where we display previous art read-only. */
  hideNextEmojis?: boolean;
  /** When showing previous art under the shift banner the caption should read
   *  read-only — `false` for the completed-today branch. */
  readOnly?: boolean;
}

/**
 * Shared "art box": title row, image with emojis overlay, caption, quote,
 * reactions, comments, stats. The monolith duplicates this between the
 * `completed` branch (lines 1599–1693) and the `not_started + scheduled`
 * branch (lines 1402–1525). One component now serves both.
 */
export const AotdArtCard: React.FC<AotdArtCardProps> = ({
  artInfo,
  artUrl,
  streakIcon,
  generationElapsedTime,
  hasPermission,
  isAuthenticated,
  currentUsername,
  onTriggerWayback,
  hideNextEmojis,
  readOnly,
}) => {
  const {
    title,
    createdBy,
    authorQuote,
    quoteHelp,
    currentStreakNum,
    thisAOTDEmojis,
    emojisHidden,
    hiddenEmojiCount,
    nextAOTDEmojis,
    engineVersion,
    isBirthday,
    origin,
  } = artInfo;

  return (
    <div className="art-box">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {title ? (
            <h3 className="text-lg font-semibold">{title}</h3>
          ) : (
            <div className="text-placeholder title-placeholder h-6 w-1/2 bg-gray-300 rounded animate-pulse" />
          )}
          {isBirthday ? (
            <span
              title="Birthday priority — this day was claimed by the birthday user"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "2px",
                flexShrink: 0,
                fontSize: "11px",
                fontWeight: 500,
                lineHeight: 1,
                color: "#be185d",
                background: "#fce7f3",
                padding: "3px 8px",
                borderRadius: "9999px",
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              🎂 birthday
            </span>
          ) : null}
        </div>
        {streakIcon && typeof currentStreakNum === "number" && currentStreakNum > 3 ? (
          <div className="streak-container flex items-center">
            <img
              src={streakIcon.toString()}
              alt="Streak Icon"
              style={{ width: "24px", height: "24px", marginRight: "2px" }}
            />
            <span className="font-bold text-sm">{currentStreakNum}</span>
          </div>
        ) : null}
      </div>

      <div style={{ position: "relative" }}>
        {artUrl ? (
          <img
            src={artUrl}
            alt={`Art of the day: ${title || "Loading..."}`}
            className="w-full h-auto"
          />
        ) : (
          <div className="text-placeholder w-full h-64 bg-gray-300 rounded animate-pulse" />
        )}
        {emojisHidden ? (
          <EmojisBox emojis={[]} hiddenCount={hiddenEmojiCount ?? 3} onHiddenClick={scrollToGuessing} />
        ) : (
          thisAOTDEmojis && thisAOTDEmojis.length > 0 && <EmojisBox emojis={thisAOTDEmojis} />
        )}
        {engineVersion ? (
          <span
            title="AOTD engine model version"
            style={{
              position: "absolute",
              bottom: "-2px",
              left: "-3px",
              fontSize: "10px",
              lineHeight: 1,
              color: "#6b7280",
              background: "rgba(243, 244, 246, 0.8)",
              padding: "2px 4px",
              borderRadius: "2px",
              userSelect: "none",
              pointerEvents: "none",
            }}
          >
            aotd-{engineVersion}
          </span>
        ) : null}
      </div>

      {createdBy ? (
        <div className="image-caption text-sm text-gray-600 mt-1">
          <em>author: art initialized by <CliqueAttribution origin={origin} fallback={createdBy} /></em>
        </div>
      ) : !readOnly ? (
        <div className="text-placeholder caption-placeholder h-4 w-1/3 bg-gray-300 rounded mt-1 animate-pulse" />
      ) : null}

      {authorQuote ? (
        <div className="quote">
          <br />
          {authorQuote}
          <span className="author">
            - <CliqueAttribution origin={origin} fallback={createdBy || "..."} />
            {(quoteHelp ?? 0) > 0 ? " & AI" : ""}
          </span>
        </div>
      ) : null}

      {artInfo.aotdId && (
        <ReactionBar
          targetType="aotd"
          targetId={artInfo.aotdId}
          isAuthenticated={isAuthenticated}
        />
      )}

      {artInfo.aotdId && (
        <div
          style={{
            border: "2px solid #e5e7eb",
            borderRadius: "8px",
            padding: "12px",
            marginTop: "16px",
            backgroundColor: "rgba(249, 250, 251, 0.5)",
          }}
        >
          {isAuthenticated ? (
            <AOTDComments
              aotdId={artInfo.aotdId}
              currentUsername={currentUsername}
              isAuthenticated={isAuthenticated}
            />
          ) : (
            <SignedOutComments aotdId={artInfo.aotdId} />
          )}
        </div>
      )}

      <AOTDStats
        generationElapsedTime={generationElapsedTime}
        generationTimeMs={artInfo.generationTime}
        nextAOTDEmojis={hideNextEmojis ? undefined : nextAOTDEmojis}
        views={artInfo.views}
        hasPermission={readOnly ? false : hasPermission}
        verifier={<Verifier info={artInfo} onTriggerWayback={() => void onTriggerWayback()} />}
        authorViewTime={artInfo.authorViewTime}
        createdBy={artInfo.createdBy}
        origin={origin}
        race={artInfo.race}
        isBirthday={isBirthday}
      />

      {!readOnly && artInfo.race ? <RaceAuditPanel artInfo={artInfo} /> : null}
    </div>
  );
};

export default AotdArtCard;
